/**
 * Centralized account upgrade utilities
 *
 * This module provides a single source of truth for user type upgrades (Creator/Client).
 * Used by both router guards and AccountCreation.vue to ensure consistent behavior.
 */

import UserService from '@/services/api/user.service'
import { getTargetCreator, clearTargetCreator } from '@/utils/clientIntent'

/**
 * Upgrade a user to Creator type
 *
 * @param {Object} options - Upgrade options
 * @param {string} [options.studioInviteToken] - Studio invite token if present
 * @param {string} [options.partnerUsername] - Partner username if present
 * @param {Object} [options.phone] - Phone object { number, countryCode } if present
 * @returns {Promise<Object>} - API response from upgradeToCreator
 */
export async function upgradeUserToCreator(options = {}) {
    // Auto-read stored data for fallbacks
    const storedData = getSignupData()
    const storedPhone = getPhoneFromStorage()

    const {
        studioInviteToken = storedData.studioInviteToken,
        partnerUsername = storedData.referralId || storedData.partnerUsername,
        phone = storedPhone,
    } = options

    const userData = {}

    // Add studio invite token if present
    if (studioInviteToken) {
        userData.studioInviteToken = studioInviteToken
    }

    // Add partner referral (referralId or partnerUsername) if present
    if (partnerUsername) {
        userData.partnerUsername = partnerUsername
    }

    // Add phone if present (from WhatsApp step)
    if (phone?.number) {
        userData.phone = phone
    }

    return await UserService.upgradeToCreator(userData)
}

/**
 * Upgrade a user to Client type
 *
 * @param {Object} options - Upgrade options
 * @param {string} [options.displayname] - Display name to set
 * @param {string} [options.refCode] - Referral code if present
 * @param {Object} [options.authStore] - Auth store instance for updating user data
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function upgradeUserToClient(options = {}) {
    // Auto-read stored data for fallbacks
    const storedData = getSignupData()

    const {
        displayname = storedData.pendingDisplayname,
        refCode = storedData.refCode,
        authStore,
    } = options

    try {
        // Update displayname/username if provided
        if (displayname) {
            const username = displayname
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/-+$/g, '')

            await UserService.updateUser({
                displayname: displayname.trim(),
                username: username,
                acceptedTerms: true, // Clients auto-accept terms
            })

            // Small delay to avoid MongoDB write conflict
            await new Promise((resolve) => setTimeout(resolve, 300))
        } else {
            // Even without displayname, set acceptedTerms for clients
            await UserService.updateUser({
                acceptedTerms: true,
            })
        }

        // Upgrade to client
        await UserService.upgradeToClient()

        // Handle referral code if present
        if (refCode) {
            try {
                const referralApi = (
                    await import('@/services/api/referral.service')
                ).default
                const referralResult =
                    await referralApi.createClientReferral(refCode)
                if (referralResult?.success) {
                    localStorage.removeItem('loginTargetCreatorRefCode')
                }
            } catch (e) {
                console.warn('Referral failed:', e)
            }
        }

        // Fetch fresh user data
        const freshUser = await UserService.getCurrentUser()
        if (freshUser?.status === 200 && freshUser.data) {
            if (authStore?.user?.data) {
                authStore.user.data = freshUser.data
                authStore.user.data.typeofuser = 'Client'
                // Note: finishedSetup is managed by backend via recomputeClientSetupStage
                // It will be false if preferences are not yet complete
            }
        }

        // Conversation creation happens after pre-chat setup

        return { success: true }
    } catch (error) {
        console.error('Client upgrade failed:', error)
        return { success: false, error: error.message }
    }
}

/**
 * Get signup data from localStorage and URL params
 *
 * @param {URLSearchParams} [urlParams] - URL search params (optional)
 * @returns {Object} - Signup data object
 */
export function getSignupData(urlParams = null) {
    const params = urlParams || new URLSearchParams(window.location.search)
    const targetCreator = getTargetCreator()

    return {
        signupIntent: localStorage.getItem('signupIntent'),
        studioInviteToken:
            params.get('studioInvite') || localStorage.getItem('studioInvite'),
        referralId: params.get('new') || localStorage.getItem('referralId'),
        partnerUsername:
            params.get('partner') || localStorage.getItem('partnerUsername'),
        /** Creator-to-creator referral code (from ?invite=... on creator landing) */
        invite: params.get('invite') || localStorage.getItem('invite'),
        /** Welcome bonus coupon code (from ?code=... on creator landing) */
        code: params.get('code') || localStorage.getItem('code'),
        pendingDisplayname: localStorage.getItem('pendingClientDisplayname'),
        targetCreatorId: targetCreator?._id,
        targetCreatorUsername: targetCreator?.username,
        refCode: localStorage.getItem('loginTargetCreatorRefCode'),
    }
}

/**
 * Get phone data from localStorage (set by WhatsApp step)
 *
 * @returns {Object|null} - Phone object { number, countryCode } or null
 */
export function getPhoneFromStorage() {
    try {
        const storedPhone = localStorage.getItem('phone')
        const storedCountryCode = localStorage.getItem('phoneCountryCode')

        if (storedPhone) {
            // Remove +, spaces, dashes, parentheses
            let normalizedPhone = storedPhone.replace(/[+\s\-()]/g, '')

            // Backward compatibility: If phone includes country code prefix, extract it
            // Check if storedCountryCode exists and if phone starts with it
            if (storedCountryCode && normalizedPhone.startsWith(storedCountryCode)) {
                // Remove country code prefix from the number
                normalizedPhone = normalizedPhone.substring(storedCountryCode.length)
            }

            if (normalizedPhone) {
                return {
                    number: normalizedPhone,
                    countryCode: storedCountryCode || null,
                }
            }
        }
    } catch (e) {
        console.error('Error reading phone from storage:', e)
    }
    return null
}

/**
 * Clear signup-related localStorage items
 *
 * @param {string} userType - 'Creator' or 'Client'
 */
export function clearSignupLocalStorage(userType) {
    try {
        // Always clear signup intent
        localStorage.removeItem('signupIntent')

        if (userType === 'Creator') {
            localStorage.removeItem('studioInvite')
            localStorage.removeItem('partnerUsername')
            localStorage.removeItem('referralId')
            localStorage.removeItem('invite')
            localStorage.removeItem('code')
        }

        if (userType === 'Client') {
            localStorage.removeItem('pendingClientDisplayname')
            localStorage.removeItem('loginTargetCreatorRefCode')
            // Using centralized intent clearing for target creator data
            clearTargetCreator()
        }
    } catch (_) {
        // Silently fail if unable to clear storage
    }
}
